import React from 'react'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import type {
  SupportCenterProfileOption,
  SupportCenterSpecializationAssignment,
  SupportCenterUserUnavailability,
} from '../../types/supportCenterSpecialties'
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { buildIsoDate, formatDateBr, parseIsoDate } from '../../components/centralAmparo/scheduleHelpers'
import {
  MODULE_NAME,
  clampDay,
  getErrorMessage,
  todayParts,
} from '../../components/centralAmparo/parametrosHelpers'
import ParametrosUnavailModal, {
  type UnavailModalState,
} from './ParametrosUnavailModal'
import ParametrosDeleteModal from './ParametrosDeleteModal'

const ParametrosIndisponibilidadeTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [assignments, setAssignments] = React.useState<SupportCenterSpecializationAssignment[]>(
    []
  )
  const [unavailability, setUnavailability] = React.useState<SupportCenterUserUnavailability[]>(
    []
  )
  const [filter, setFilter] = React.useState('')

  const [unavailModal, setUnavailModal] = React.useState<UnavailModalState | null>(null)
  const [submittingUnavail, setSubmittingUnavail] = React.useState(false)
  const [unavailError, setUnavailError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [assigns, unavails] = await Promise.all([
        supportCenterSpecialtiesService.listAssignments(),
        supportCenterSpecialtiesService.listUnavailability(),
      ])
      setAssignments(assigns)
      setUnavailability(unavails)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const assignedProfileOptions = React.useMemo(() => {
    const byId = new Map<string, SupportCenterProfileOption>()
    for (const a of assignments) {
      if (a.profile?.id) {
        byId.set(a.profile.id, { id: a.profile.id, name: a.profile.name })
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [assignments])

  const filteredUnavailability = filter.trim()
    ? unavailability.filter((u) =>
        (u.profile?.name ?? '').toLowerCase().includes(filter.trim().toLowerCase())
      )
    : unavailability

  const openNewUnavail = () => {
    const start = todayParts()
    setUnavailError(null)
    setUnavailModal({
      user_id: '',
      start,
      end: { ...start },
      all_day: true,
      hours: [],
      reason: '',
    })
  }

  const openEditUnavail = (item: SupportCenterUserUnavailability) => {
    setUnavailError(null)
    setUnavailModal({
      id: item.id,
      user_id: item.user_id,
      start: parseIsoDate(item.starts_on),
      end: parseIsoDate(item.ends_on),
      all_day: item.all_day,
      hours: item.hours ?? [],
      reason: item.reason ?? '',
    })
  }

  const closeUnavailModal = () => {
    if (submittingUnavail) return
    setUnavailModal(null)
    setUnavailError(null)
  }

  const handleUnavailSave = async () => {
    if (!unavailModal || !user?.id) return
    if (!unavailModal.user_id) {
      setUnavailError('Selecione o profissional.')
      return
    }

    const start = clampDay(unavailModal.start)
    const end = clampDay(unavailModal.end)
    const startsOn = buildIsoDate(start.year, start.month, start.day)
    const endsOn = buildIsoDate(end.year, end.month, end.day)

    if (endsOn < startsOn) {
      setUnavailError('A data final deve ser maior ou igual à data inicial.')
      return
    }
    if (!unavailModal.all_day && unavailModal.hours.length === 0) {
      setUnavailError('Selecione ao menos uma hora ou marque dia(s) inteiro(s).')
      return
    }

    setSubmittingUnavail(true)
    setUnavailError(null)
    try {
      const savedId = await supportCenterSpecialtiesService.upsertUnavailability({
        id: unavailModal.id,
        user_id: unavailModal.user_id,
        starts_on: startsOn,
        ends_on: endsOn,
        all_day: unavailModal.all_day,
        hours: unavailModal.hours,
        reason: unavailModal.reason,
        actor_id: user.id,
      })
      const profileName =
        assignedProfileOptions.find((p) => p.id === unavailModal.user_id)?.name ??
        unavailModal.user_id
      const periodLabel = `${formatDateBr(startsOn)} a ${formatDateBr(endsOn)}`
      void (unavailModal.id
        ? logUpdate(
            'support_center_user_unavailability',
            unavailModal.id,
            `Atualizou indisponibilidade: ${profileName} (${periodLabel})`
          )
        : logCreate(
            'support_center_user_unavailability',
            savedId,
            `Criou indisponibilidade: ${profileName} (${periodLabel})`
          ))
      closeUnavailModal()
      await loadData()
    } catch (error) {
      setUnavailError(getErrorMessage(error))
    } finally {
      setSubmittingUnavail(false)
    }
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      await supportCenterSpecialtiesService.softDeleteUnavailability(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      void logDelete('support_center_user_unavailability', deleteModal.id, `Excluiu indisponibilidade: ${deleteModal.label}`, {
        reason: deleteReason.trim(),
      })
      closeDeleteModal()
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      closeDeleteModal()
    } finally {
      setSubmittingDelete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Indisponibilidade
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {filteredUnavailability.length}
              {filter.trim() && filteredUnavailability.length !== unavailability.length
                ? ` de ${unavailability.length}`
                : ''}{' '}
              {filteredUnavailability.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar por profissional..."
              className="w-48 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {canInsert && (
              <Button
                variant="solid"
                size="md"
                onClick={openNewUnavail}
                disabled={assignedProfileOptions.length === 0}
              >
                <PlusIcon className="h-4 w-4" />
                Nova Indisponibilidade
              </Button>
            )}
          </div>
        </div>

        {filteredUnavailability.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              {filter.trim()
                ? `Nenhum resultado para "${filter}".`
                : 'Nenhuma indisponibilidade cadastrada.'}
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Profissional
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Período
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Motivo
                  </th>
                  {(canUpdate || canDelete) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUnavailability.map((item) => {
                  const userName = item.profile?.name ?? '—'
                  const period = `${formatDateBr(item.starts_on)} – ${formatDateBr(item.ends_on)}`
                  const typeLabel = item.all_day
                    ? 'Dia(s) inteiro(s)'
                    : item.hours.map((h) => `${h.toString().padStart(2, '0')}h`).join(', ')
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {userName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {period}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {typeLabel}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {item.reason || '—'}
                      </td>
                      {(canUpdate || canDelete) && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                title="Editar"
                                onClick={() => openEditUnavail(item)}
                                className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                title="Excluir"
                                onClick={() => {
                                  setDeleteModal({
                                    id: item.id,
                                    label: `${userName} (${period})`,
                                  })
                                  setDeleteReason('')
                                }}
                                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {unavailModal && (
        <ParametrosUnavailModal
          unavailModal={unavailModal}
          unavailError={unavailError}
          submitting={submittingUnavail}
          assignedProfileOptions={assignedProfileOptions}
          onChange={setUnavailModal}
          onClose={closeUnavailModal}
          onSave={() => void handleUnavailSave()}
        />
      )}

      {deleteModal && (
        <ParametrosDeleteModal
          label={deleteModal.label}
          deleteReason={deleteReason}
          submitting={submittingDelete}
          onReasonChange={setDeleteReason}
          onClose={closeDeleteModal}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}

export default ParametrosIndisponibilidadeTab
